// بیمه ساختمان‌ها
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, toFa, Grid, Field, Lookup, MessageBox, ChoiceBox, splitLines, visibleCols, runReport, num, fmtAmt,
} from './BuildingsShared';

const FORM = 'FRMBUILDINGINSURE';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNNEWREQUEST',
  'BTNINSUREREQUEST', 'BTNINSURECONFIRM'];

const COLS = [
  { key: 'serial_no', label: 'شماره سریال' },
  { key: 'insure_no', label: 'شماره بیمه نامه' },
  { key: 'issue_date', label: 'تاریخ صدور' },
  { key: 'amount_receiver', label: 'بیمه گزار', wrap: true },
  { key: 'from_date', label: 'از تاریخ' },
  { key: 'to_date', label: 'تا تاریخ' },
  { key: 'insure_cost', label: 'مبلغ بیمه', money: true },
  { key: 'added_tax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'sum_pay', label: 'جمع', money: true },
  { key: 'remark', label: 'شرح تعهدات', wrap: true },
  { key: 'request_no', label: 'شماره درخواست', num: true },
  { key: 'request_date', label: 'تاریخ درخواست' },
  { key: 'confirm_no', label: 'شماره تاییدیه', num: true },
  { key: 'confirm_date', label: 'تاریخ تاییدیه' },
  { key: 'hazineh_document_no', label: 'شماره سند هزینه', num: true },
  { key: 'hazineh_document_date', label: 'تاریخ سند هزینه' },
  { key: 'asker', label: 'درخواست کننده', wrap: true },
  { key: 'department', label: 'واحد اجرایی', wrap: true },
  { key: 'department_manager', label: 'مسئول واحد اجرایی', wrap: true },
  { key: 'user_group', label: 'گروه کاربری' },
];
const REQ_COLS = [{ key: 'building', label: 'ساختمان', wrap: true }, ...COLS.filter(c =>
  !['request_no', 'request_date', 'confirm_no', 'confirm_date'].includes(c.key))];

const KEYS = ['serial_no', 'insure_no', 'issue_date', 'amount_receiver_id', 'amount_receiver', 'from_date', 'to_date',
  'insure_cost', 'added_tax', 'remark', 'request_no', 'request_date', 'confirm_no', 'confirm_date', 'asker_id', 'asker',
  'department_id', 'department', 'hazineh_document_no', 'hazineh_document_date'];
const pick = r => KEYS.reduce((o, k) => ({ ...o, [k]: r ? (r[k] ?? '') : '' }), {});

function Box({ title, children }) {
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 10px', flex: 1, minWidth: '220px' }}>
      <div style={ui.flabel}>{title}</div>
      <div style={{ display: 'flex', gap: '8px' }}>{children}</div>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '130px' }}>
      <label style={ui.mlabel}>{label}</label>
      <JalaliDatePicker value={value} onChange={onChange} placeholder={label} />
    </div>
  );
}

export default function BuildingInsure({ user, onBack, building, year }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState('');
  const [lastReceiver, setLastReceiver] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [reqRows, setReqRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(pick(null));
  const [formErr, setFormErr] = useState('');
  const [newReq, setNewReq] = useState(false);
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(false);
  const [choice, setChoice] = useState(false);

  const load = useCallback(async (keepId) => {
    setLoading(true); setError('');
    const j = await API(`/${building.id}/insure/`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setRows(j.data); setTitle(j.building_title || ''); setLastReceiver(j.last_amount_receiver_id || 0);
    const hit = keepId ? j.data.find(r => r.id === keepId) : null;
    setSelected(hit || j.data[0] || null);
  }, [building]);

  useEffect(() => { if (fb.loaded && fb.open) load(); }, [fb.loaded, fb.open]);

  useEffect(() => {
    if (!selected) { setReqRows([]); return; }
    API(`/insure/request-items/?request_no=${selected.request_no}&request_date=${encodeURIComponent(selected.request_date)}`)
      .then(j => setReqRows(j.ok ? j.data : []));
  }, [selected]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const has = !!selected;
  const sumPay = num(form.insure_cost) + num(form.added_tax);

  const openNew = async () => {
    const j = await API(`/insure/defaults/?year=${year}`);
    if (!j.ok) { showMsg(j.msg); return; }
    const s = selected || {};
    setForm({ ...pick(null), ...j.data,
      amount_receiver_id: s.amount_receiver_id || lastReceiver, amount_receiver: s.amount_receiver || '',
      asker_id: s.asker_id || 0, asker: s.asker || '', department_id: s.department_id || 0, department: s.department || '' });
    setFormErr(''); setNewReq(true); setModal('new');
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const openEdit = async () => {
    if (!selected) return;
    const j = await API(`/insure/${selected.id}/check-edit/`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(pick(selected)); setFormErr(''); setNewReq(false); setModal('edit');
  };
  const save = async () => {
    setFormErr('');
    const body = JSON.stringify({ ...form, building_id: building.id, year });
    const j = modal === 'new'
      ? await API('/insure/create/', { method: 'POST', body })
      : await API(`/insure/${selected.id}/update/`, { method: 'PUT', body });
    if (j.ok) {
      setModal(null); load(j.id);
      if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
    } else {
      setFormErr(j.msg);
      if (j.need_new_request) setNewReq(true);
    }
  };
  const newRequestNo = async () => {
    const j = await API(`/request-no/?year=${year}&request_date=${encodeURIComponent(form.request_date || '')}&confirm_date=${encodeURIComponent(form.confirm_date || '')}`);
    if (!j.ok) { showMsg(j.msg); return; }
    setForm(f => ({ ...f, request_no: j.request_no, confirm_no: j.confirm_no }));
    if (j.messages && j.messages.length) showMsg(j.messages.join('\n'));
  };
  const taxKey = e => {
    if (e.key === 'F11') {
      e.preventDefault();
      const p = num(form.added_tax);
      if (p < 100) F('added_tax', String(Math.round(num(form.insure_cost) * p / 100)));
    }
  };
  const doDelete = async () => {
    setAsk(false);
    const j = await API(`/insure/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) load(); else showMsg(j.msg);
  };

  const pageTitle = `بیمه نامه ${title}`;
  const shown = visibleCols(COLS, rows);
  const toolbar = (
    <>
      {fb.open && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>＋ جدید</button>}
          {fb.can('BTNEDIT') && rows.length > 0 && <button onClick={openEdit} disabled={!has} style={{ ...ui.btnEdit, cursor: 'pointer' }}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && rows.length > 0 && <button onClick={() => has && setAsk(true)} disabled={!has} style={{ ...ui.btnDel, cursor: 'pointer' }}>✕ حذف</button>}
          {fb.can('BTNINSUREREQUEST') && has && (
            <button onClick={() => runReport('tadarokat.building_insure_request_one',
              { request_no: selected.request_no, request_date: selected.request_date }, showMsg)}
            style={{ ...ui.btnItems, cursor: 'pointer' }}>برگه درخواست</button>)}
          {fb.can('BTNINSURECONFIRM') && has && (
            <button onClick={() => setChoice(true)} style={{ ...ui.btnItems, cursor: 'pointer' }}>تاییدیه</button>)}
        </div>
      )}
      <ExcelButton title={pageTitle} columns={shown} rows={rows} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={pageTitle} subtitle="تدارکات — سامانه ارجان"
        toolbar={toolbar} error={error} loading={loading}>
        <div style={ui.splitWrap}>
          <div style={ui.topPane}>
            <Grid columns={shown} rows={rows} selectedId={selected && selected.id} onSelect={setSelected}
              loading={loading} empty="بیمه نامه ای ثبت نشده است" />
          </div>
          <div style={ui.bottomPane}>
            {reqRows.length > 0 && selected && (
              <>
                <div style={{ padding: '6px 10px', fontSize: '12px', fontWeight: 700, color: '#1a2f50' }}>
                  {`ریز درخواست شماره ${toFa(selected.request_no)} تاریخ ${toFa(selected.request_date)}`}
                </div>
                <Grid columns={visibleCols(REQ_COLS, reqRows)} rows={reqRows} zebra="#f5f5dc" />
              </>
            )}
          </div>
        </div>
      </ListPage>

      {modal && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? 'بیمه نامه جدید' : 'ویرایش بیمه نامه'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {formErr && <div style={{ ...ui.errBox, whiteSpace: 'pre-line' }}>⚠️ {formErr}</div>}
              <div style={ui.row}>
                <Field label="شماره بیمه نامه" value={form.insure_no} onChange={v => F('insure_no', v)} ltr />
                <Field label="شماره سریال" value={form.serial_no} onChange={v => F('serial_no', v)} ltr />
                <DateField label="تاریخ صدور" value={form.issue_date} onChange={v => F('issue_date', v)} />
              </div>
              <div style={ui.row}>
                <Lookup label="کارگزار بیمه" value={form.amount_receiver} endpoint="/lookup/amount-receivers/"
                  onPick={r => setForm(f => ({ ...f, amount_receiver_id: r.id, amount_receiver: r.title }))} />
                <DateField label="از تاریخ" value={form.from_date} onChange={v => F('from_date', v)} />
                <DateField label="تا تاریخ" value={form.to_date} onChange={v => F('to_date', v)} />
              </div>
              <div style={ui.row}>
                <Field label="مبلغ" value={form.insure_cost} onChange={v => F('insure_cost', v)} ltr />
                <Field label="مالیات بر ارزش افزوده" value={form.added_tax} onChange={v => F('added_tax', v)} onKeyDown={taxKey} ltr />
                <Field label="جمع" value={fmtAmt(sumPay)} disabled />
              </div>
              <div style={ui.row}>
                <Field label="شرح تعهدات" value={form.remark} onChange={v => F('remark', v)} />
              </div>
              <div style={ui.row}>
                <Lookup label="درخواست کننده" value={form.asker} endpoint="/lookup/people/"
                  onPick={r => setForm(f => ({ ...f, asker_id: r.id, asker: r.title }))} />
                <Lookup label="واحد اجرایی" value={form.department} endpoint="/lookup/departments/"
                  onPick={r => setForm(f => ({ ...f, department_id: r.id, department: r.title }))} />
              </div>
              <div style={ui.row}>
                <Box title="درخواست">
                  <Field label="شماره" value={form.request_no} onChange={v => F('request_no', v)} ltr />
                  <DateField label="تاریخ" value={form.request_date} onChange={v => F('request_date', v)} />
                </Box>
                <Box title="تایید">
                  <Field label="شماره" value={form.confirm_no} onChange={v => F('confirm_no', v)} ltr />
                  <DateField label="تاریخ" value={form.confirm_date} onChange={v => F('confirm_date', v)} />
                </Box>
                <Box title="سند هزینه">
                  <Field label="شماره" value={form.hazineh_document_no || ''} disabled />
                  <Field label="تاریخ" value={form.hazineh_document_date || ''} disabled />
                </Box>
              </div>
            </div>
            <div style={ui.mFooter}>
              {newReq && fb.can('BTNOK') && <button onClick={newRequestNo} style={{ ...ui.btnItems, cursor: 'pointer' }}>شماره درخواست جدید</button>}
              {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
              {fb.can('BTNOK') && <button onClick={save} style={ui.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {choice && <ChoiceBox options={['صورتجلسه مصرف', 'تاییدیه']} onCancel={() => setChoice(false)}
        onPick={o => { setChoice(false); runReport('tadarokat.building_insure_confirm_one', { confirm_no: selected.confirm_no,
          confirm_date: selected.confirm_date, request_date: selected.request_date, is_confirm: o === 'تاییدیه' ? 1 : 0 }, showMsg); }} />}
      {ask && <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر"
        onConfirm={doDelete} onCancel={() => setAsk(false)} tone="danger" />}
      {msg && <MessageBox lines={msg} onClose={() => setMsg(null)} />}
    </>
  );
}
